import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { TitularResiduosPeligrososViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-titular-residuos-peligrosos-autocomplete",
    templateUrl: "./titular-residuos-peligrosos-autocomplete.component.html",
    styleUrls: ["./titular-residuos-peligrosos-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TitularResiduosPeligrososAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TitularResiduosPeligrososAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Titular Residuos Peligrosos";
	@Input() placeholder =
		"Buscar titular de residuos peligrosos por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint =
		"Busque por código o descripción de titular de residuos peligrosos";

	// Outputs
	@Output() titularResiduosPeligrososSelected =
		new EventEmitter<TitularResiduosPeligrososViewModel>();

	// State
	titularesResiduosPeligrosos: TitularResiduosPeligrososViewModel[] = [];
	titularResiduosPeligrososId: number | null = null;
	selectedTitularResiduosPeligrosos: TitularResiduosPeligrososViewModel | null =
		null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadTitularesResiduosPeligrosos();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.titularResiduosPeligrososId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de titulares de residuos peligrosos
	 */
	private loadTitularesResiduosPeligrosos(): void {
		this.masterService
			.getTitularResiduosPeligrososList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (titularesResiduosPeligrosos) => {
					this.titularesResiduosPeligrosos =
						titularesResiduosPeligrosos;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los titulares de residuos peligrosos",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedTitularResiduosPeligrosos =
			this.titularResiduosPeligrososId != null
				? (this.titularesResiduosPeligrosos.find(
						(t) => t.id === this.titularResiduosPeligrososId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un titular de residuos peligrosos
	 */
	displayTitularResiduosPeligrosos = (
		titularResiduosPeligrosos: TitularResiduosPeligrososViewModel | string,
	): string => {
		if (typeof titularResiduosPeligrosos === "string") {
			return titularResiduosPeligrosos;
		}
		return titularResiduosPeligrosos &&
			titularResiduosPeligrosos.codigo &&
			titularResiduosPeligrosos.descripcion
			? `${titularResiduosPeligrosos.codigo} - ${titularResiduosPeligrosos.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(
		titularResiduosPeligrosos: TitularResiduosPeligrososViewModel | null,
	): void {
		this.titularResiduosPeligrososId = titularResiduosPeligrosos
			? titularResiduosPeligrosos.id
			: null;
		this.selectedTitularResiduosPeligrosos = titularResiduosPeligrosos;
		this.onChange(this.titularResiduosPeligrososId);
		this.onTouched();
		if (titularResiduosPeligrosos) {
			this.titularResiduosPeligrososSelected.emit(
				titularResiduosPeligrosos,
			);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
